import React, { useState, useEffect, useCallback } from "react";
import { X, Loader2, Plus, RefreshCw, Trash2, CheckCircle, Clock, XCircle, ArrowLeft } from "lucide-react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const STATUS_CONFIG = {
  APPROVED: { label: "Aprobada", icon: CheckCircle, cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  PENDING: { label: "En revisión", icon: Clock, cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  REJECTED: { label: "Rechazada", icon: XCircle, cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  PAUSED: { label: "Pausada", icon: Clock, cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" },
  DISABLED: { label: "Deshabilitada", icon: XCircle, cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" }
};

const CATEGORIES = [
  { value: "UTILITY", label: "Utilidad (seguimiento, citas, avisos)" },
  { value: "MARKETING", label: "Marketing (promociones, novedades)" }
];

const LANGUAGES = [
  { value: "es", label: "Español" },
  { value: "es_MX", label: "Español (México)" },
  { value: "en_US", label: "Inglés (EE. UU.)" }
];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, icon: Clock, cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

const getBodyText = (template) =>
  template?.components?.find((c) => c.type === "BODY")?.text || "";

const emptyForm = { name: "", category: "UTILITY", language: "es", bodyText: "", footerText: "" };

const WhatsAppCloudTemplateManager = ({ open, onClose, whatsappId, connectionName }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [view, setView] = useState("list"); // "list" | "create"
  const [form, setForm] = useState(emptyForm);
  const [examples, setExamples] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const loadTemplates = useCallback(() => {
    if (!whatsappId) return;
    setLoading(true);
    api.get(`/api/whatsapp-cloud/templates/${whatsappId}`, { params: { all: "true" } })
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [whatsappId]);

  useEffect(() => {
    if (!open) return;
    setView("list");
    setForm(emptyForm);
    setExamples({});
    loadTemplates();
  }, [open, loadTemplates]);

  if (!open) return null;

  const variableKeys = (() => {
    const keys = [];
    const regex = /\{\{(\d+)\}\}/g;
    let match;
    while ((match = regex.exec(form.bodyText)) !== null) {
      const num = parseInt(match[1], 10);
      if (!keys.includes(num)) keys.push(num);
    }
    return keys.sort((a, b) => a - b);
  })();

  const handleFieldChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    if (!form.bodyText.trim()) { toast.error("El contenido del mensaje es requerido"); return; }

    const exampleValues = [];
    for (const key of variableKeys) {
      const val = examples[key];
      if (!val || !val.trim()) {
        toast.error(`Proporciona un valor de ejemplo para la variable {{${key}}} (Meta lo exige para revisar la plantilla)`);
        return;
      }
      exampleValues.push(val.trim());
    }

    setSubmitting(true);
    try {
      await api.post(`/api/whatsapp-cloud/templates/${whatsappId}`, {
        name: form.name,
        category: form.category,
        language: form.language,
        bodyText: form.bodyText,
        footerText: form.footerText,
        examples: exampleValues
      });
      toast.success("Plantilla enviada a revisión de Meta. Verás el estado aquí mismo.");
      setView("list");
      setForm(emptyForm);
      setExamples({});
      loadTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (template) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Eliminar la plantilla "${template.name}"? Esta acción también la elimina en Meta.`)) return;
    setDeleting(template.name);
    try {
      await api.delete(`/api/whatsapp-cloud/templates/${whatsappId}/${template.name}`);
      toast.success("Plantilla eliminada");
      loadTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            {view === "create" && (
              <button onClick={() => setView("list")} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {view === "list" ? `Plantillas de WhatsApp — ${connectionName || ""}` : "Solicitar nueva plantilla"}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {view === "list" && (
              <>
                <button
                  onClick={loadTemplates}
                  disabled={loading}
                  title="Actualizar estados desde Meta"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
                <button
                  onClick={() => setView("create")}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Nueva
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto bg-slate-50 dark:bg-slate-800/10">
          {view === "list" ? (
            loading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                <p className="text-xs text-slate-500">Consultando plantillas en Meta...</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <p className="text-sm">Aún no hay plantillas en esta cuenta.</p>
                <p className="text-xs text-slate-400 mt-1">Crea la primera con el botón "Nueva".</p>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map((t) => (
                  <div key={t.id || `${t.name}_${t.language}`} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{t.name}</span>
                        <span className="text-[11px] text-slate-400 flex-shrink-0">{t.language} · {t.category}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusBadge status={t.status} />
                        <button
                          onClick={() => handleDelete(t)}
                          disabled={deleting === t.name}
                          title="Eliminar plantilla"
                          className="p-1 rounded text-slate-300 hover:text-red-500 transition-colors"
                        >
                          {deleting === t.name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                    {getBodyText(t) && (
                      <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 leading-relaxed whitespace-pre-wrap line-clamp-3">
                        {getBodyText(t)}
                      </p>
                    )}
                    {t.status === "REJECTED" && t.rejected_reason && t.rejected_reason !== "NONE" && (
                      <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">
                        Motivo del rechazo: {t.rejected_reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Nombre interno
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFieldChange}
                  placeholder="ej. recordatorio_cita"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                />
                <p className="mt-1 text-[11px] text-slate-400">Solo minúsculas, números y guiones bajos (se normaliza automáticamente).</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Categoría
                  </label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleFieldChange}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Idioma
                  </label>
                  <select
                    name="language"
                    value={form.language}
                    onChange={handleFieldChange}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Contenido del mensaje
                </label>
                <textarea
                  name="bodyText"
                  value={form.bodyText}
                  onChange={handleFieldChange}
                  rows={5}
                  placeholder={"Hola {{1}}, le recordamos su cita del {{2}} en Centro Médico Siglo 21."}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all resize-y"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  {"Usa {{1}}, {{2}}... para partes variables (nombre del paciente, fecha, etc.)."}
                </p>
              </div>

              {variableKeys.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Valores de ejemplo (los revisa Meta)
                  </p>
                  {variableKeys.map((key) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="w-12 text-xs font-bold text-indigo-600 dark:text-indigo-400 text-right">
                        {"{{"}{key}{"}}"}
                      </span>
                      <input
                        type="text"
                        value={examples[key] || ""}
                        onChange={(e) => setExamples((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder={key === 1 ? "ej. Juan Pérez" : "ej. 20 de julio, 10:00 AM"}
                        className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Pie de mensaje (opcional)
                </label>
                <input
                  type="text"
                  name="footerText"
                  value={form.footerText}
                  onChange={handleFieldChange}
                  placeholder="ej. Centro Médico Siglo 21"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {view === "create" && (
          <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-[11px] text-slate-400 leading-snug">
              Meta revisa cada plantilla (minutos a 24-48 h). El estado aparecerá en la lista.
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setView("list")}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={submitting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Enviar a revisión
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppCloudTemplateManager;
